import * as Sentry from '@sentry/react';
import { AUTH_ROUTES } from 'pages/Auth/Auth';
import { lazy, memo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { createModal } from '@/components/common/Modal/ModalBuilder';
import { ModalContext } from '@/components/common/Modal/ModalProvider';
import useOnboardingSurveyRedirect from '@/hooks/useOnboardingSurveyRedirect/useOnboardingSurveyRedirect';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import hotjarScript from '@/hotjar/hotjar';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import { ADMINISTRATOR_PANEL_ROUTES } from '@/pages/AdministratorPanel/constants';
import { onboardingInitiation } from '@/pages/Dashboard/components/Onboarding/onboardingUtils';
import ProtectedRoute from '@/pages/Routing/ProtectedRoute/ProtectedRoute';
import ActivatedRedirectRoute from '@/pages/Routing/RedirectRoute/ActivatedRedirectRoute';
import StripePaymentStatusRoute from '@/pages/Routing/RedirectRoute/StripePaymentStatusRoute';

import { CalendarNewRequestProvider } from '../Calendar/context/calendarNewRequestContext';
import { RequestDetailsAcceptanceProvider } from '../Calendar/context/requestDetailsAcceptanceContext';

const Dashboard = lazy(() => import(/* webpackChunkName: "Dashboard" */ 'pages/Dashboard/Dashboard'));
const Employees = lazy(() => import(/* webpackChunkName: "Employees" */ 'pages/Employees/Employees'));
const Calendar = lazy(() => import(/* webpackChunkName: "Employees" */ 'pages/Calendar/Calendar'));
const MyProfile = lazy(() => import(/* webpackChunkName: "Dashboard" */ 'pages/Profile/MyProfile/MyProfile'));
const UserProfile = lazy(() => import(/* webpackChunkName: "Dashboard" */ 'pages/Profile/UserProfile/UserProfile'));
const AdministratorPanel = lazy(
    () => import(/* webpackChunkName: "AdministratorPanel" */ '@/pages/AdministratorPanel/AdministratorPanel')
);
const NewRequest = lazy(() => import(/* webpackChunkName: "NewRequest" */ 'pages/NewRequest/NewRequest'));
const Requests = lazy(() => import(/* webpackChunkName: "Requests" */ '@/pages/Requests/Requests'));
const Settings = lazy(() => import(/* webpackChunkName: "NewRequest" */ 'pages/Settings/Settings'));
const Integrations = lazy(() => import(/* webpackChunkName: "NewRequest" */ 'pages/integrations/Integrations'));
const Auth = lazy(() => import(/* webpackChunkName: "Auth" */ 'pages/Auth/Auth'));
const Page404 = lazy(() => import(/* webpackChunkName: "Page404" */ 'pages/Page404/Page404'));
const Logout = lazy(() => import(/* webpackChunkName: "Logout" */ 'pages/Logout/Logout'));
const RequestApproval = lazy(
    () => import(/* webpackChunkName: "RequestApproval" */ '@/pages/Requests/RequestApproval')
);
const RegisterOrganizationAndUser = lazy(
    () =>
        import(
            /* webpackChunkName: "RegisterOrganizationAndUser" */ '@/pages/Auth/RegisterOrganizationAndUser/RegisterOrganizationAndUser'
        )
);
const Payment = lazy(
    () => import(/* webpackChunkName: "Payment" */ '@/pages/Auth/RegisterOrganizationAndUser/PlanPayment/Payment')
);
const PaymentSuccess = lazy(
    () =>
        import(/* webpackChunkName: "PaymentSuccess" */ '@/pages/Auth/RegisterOrganizationAndUser/PlanPayment/Success')
);
const StripePaymentResult = lazy(
    () => import(/* webpackChunkName: "Payment" */ '@/pages/Auth/StripePaymentResult/StripePaymentResult')
);
const CompleteProfile = lazy(
    () => import(/* webpackChunkName: "CompleteProfile" */ '@/pages/CompleteProfile/CompleteProfile')
);
const RegistrationSurvey = lazy(
    () => import(/* webpackChunkName: "RegistrationSurvey" */ '@/pages/RegistrationSurvey/RegistrationSurvey')
);
const GoogleCallback = lazy(
    () => import(/* webpackChunkName: "GoogleCallback" */ '@/pages/Auth/GoogleCallback/GoogleCallback')
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

interface IRoutingProps {
    className?: string;
}

export enum ROUTES {
    DEFAULT_ROUTE = '/',
    MY_PROFILE = '/my-profile',
    USER_PROFILE = '/user-profile/:userId',
    DASHBOARD = '/dashboard',
    REQUEST_APPROVE = '/request-approve/:absenceId',
    REQUEST_REJECT = '/request-reject/:absenceId',
    REQUEST_REMOVAL_APPROVE = '/request-removal-approve/:absenceId',
    REQUEST_REMOVAL_REJECT = '/request-removal-reject/:absenceId',
    EMPLOYEES = '/employees',
    REQUESTS = '/requests',
    NEW_REQUEST = '/new-request/:userId',
    ADMINISTRATOR_PANEL = '/administrator-panel',
    FORGET_PASSWORD = '/forget-password',
    SETTINGS = '/settings',
    AUTH = '/auth',
    PAGE_NOT_FOUND = '/404',
    LOGOUT = '/logout',
    PAYMENT = '/payment',
    CALENDAR = '/calendar',
    INTEGRATIONS = '/integrations',
    COMPLETE_PROFILE = '/complete-profile',
    REGISTRATION_SURVEY = '/registration-survey',
    GOOGLE_CALLBACK = '/auth/google/callback',
}

export const REQUEST_ROUTES = {
    upcoming: 'upcoming',
    archive: 'archive',
    to_accept: 'to_accept',
};

// AUTH_ROUTES.LOGIN value ('/login') is inlined to avoid circular dependency at module init time
export const DEFAULT_GUEST_ROUTE = `${ROUTES.AUTH}/login`;
export const DEFAULT_PROTECTED_ROUTE = ROUTES.DASHBOARD;
export const SUBSCRIPTIONS_ROUTE = `${ROUTES.ADMINISTRATOR_PANEL}/${ADMINISTRATOR_PANEL_ROUTES.subscriptions}`;

export const trimPath = (path: string) => `${process.env.PUBLIC_URL}${path}`;

const useNonAdminWarningModal = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { open, close } = useContext(ModalContext);

    const warningModal = createModal()
        .withHeroTitle(t('common_one_moment'))
        .withContent(t('administratorPanel_subscription_not_enough_rights'))
        .withPrimaryAction({
            label: t('button_text_close'),
            onClick: () => close(),
        })
        .withSecondaryAction({
            label: t('common_logout'),
            onClick: () => {
                close();
                navigate(ROUTES.LOGOUT);
            },
        });

    const openWarningModal = () => open(warningModal);

    return openWarningModal;
};

const Routing = ({ className }: IRoutingProps) => {
    const { isLead, isCompanyOwner } = useLoggedInUserData();
    useOnboardingSurveyRedirect();

    useEffect(() => {
        const isAccessToken = localStorage.getItem('AUTH_ACCESS_TOKEN');
        const isRefreshToken = localStorage.getItem('AUTH_REFRESH_TOKEN');
        isAccessToken && isRefreshToken && mixpanelTracking(MIXPANEL_ACTIONS.OPEN_APP);
        document.head.appendChild(hotjarScript);
    }, []);

    useEffect(() => {
        onboardingInitiation(isCompanyOwner);
    }, [isCompanyOwner]);

    const openWarningModal = useNonAdminWarningModal();

    return (
        <div className={className}>
            <SentryRoutes>
                <Route
                    path={ROUTES.DASHBOARD}
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.REQUEST_REMOVAL_APPROVE}
                    element={
                        <ProtectedRoute>
                            <RequestApproval action="approve" isRemoval />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.REQUEST_REMOVAL_REJECT}
                    element={
                        <ProtectedRoute>
                            <RequestApproval action="reject" isRemoval />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.REQUEST_APPROVE}
                    element={
                        <ProtectedRoute>
                            <RequestApproval action="approve" />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.REQUEST_REJECT}
                    element={
                        <ProtectedRoute>
                            <RequestApproval action="reject" />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.EMPLOYEES}
                    element={
                        <ProtectedRoute>
                            <Employees />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.CALENDAR}
                    element={
                        <RequestDetailsAcceptanceProvider>
                            <CalendarNewRequestProvider>
                                <Calendar />
                            </CalendarNewRequestProvider>
                        </RequestDetailsAcceptanceProvider>
                    }
                />
                <Route
                    path={ROUTES.MY_PROFILE}
                    element={
                        <ProtectedRoute>
                            <MyProfile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.USER_PROFILE}
                    element={
                        <ProtectedRoute>
                            <UserProfile />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={`${ROUTES.ADMINISTRATOR_PANEL}/*`}
                    element={
                        <StripePaymentStatusRoute>
                            <ProtectedRoute isAdminRoute onReject={openWarningModal}>
                                <AdministratorPanel />
                            </ProtectedRoute>
                        </StripePaymentStatusRoute>
                    }
                />
                <Route
                    path={`${ROUTES.REQUESTS}/*`}
                    element={
                        <ProtectedRoute>
                            <Requests showAcceptTab={isLead} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.NEW_REQUEST}
                    element={
                        <ProtectedRoute>
                            <NewRequest />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.SETTINGS}
                    element={
                        <ProtectedRoute>
                            <Settings />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.INTEGRATIONS}
                    element={
                        <ProtectedRoute>
                            <Integrations />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${ROUTES.AUTH}${AUTH_ROUTES.REGISTER_ORGANIZATION_AND_USER}`}
                    element={
                        <ActivatedRedirectRoute path={SUBSCRIPTIONS_ROUTE}>
                            <RegisterOrganizationAndUser />
                        </ActivatedRedirectRoute>
                    }
                />
                <Route
                    path={`${ROUTES.AUTH}${AUTH_ROUTES.REGISTER_ORGANIZATION_AND_USER}${ROUTES.PAYMENT}/success`}
                    element={<PaymentSuccess />}
                />
                <Route
                    path={`${ROUTES.AUTH}${AUTH_ROUTES.REGISTER_ORGANIZATION_AND_USER}${ROUTES.PAYMENT}/*`}
                    element={<Payment />}
                />
                <Route path={`${ROUTES.AUTH}${AUTH_ROUTES.STRIPE_STATUS}/*`} element={<StripePaymentResult />} />
                <Route path={ROUTES.GOOGLE_CALLBACK} element={<GoogleCallback />} />
                <Route path={`${ROUTES.AUTH}/*`} element={<Auth />} />
                {/* Temporal route until the One-go-flow would become dominant for org registration process */}
                <Route
                    path={ROUTES.COMPLETE_PROFILE}
                    element={
                        <ProtectedRoute>
                            <CompleteProfile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTES.REGISTRATION_SURVEY}
                    element={
                        <ProtectedRoute>
                            <RegistrationSurvey />
                        </ProtectedRoute>
                    }
                />
                <Route path={ROUTES.PAGE_NOT_FOUND} element={<Page404 />} />
                <Route path={ROUTES.LOGOUT} element={<Logout />} />
                <Route path={ROUTES.DEFAULT_ROUTE} element={<Navigate to={DEFAULT_GUEST_ROUTE} />} />
                <Route path="*" element={<Navigate to={ROUTES.PAGE_NOT_FOUND} />} />
            </SentryRoutes>
        </div>
    );
};

const Memoized = memo(Routing);
const Styled = styled(Memoized)``;
export default Styled;
